import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { FAQEntry, PlatformSettings } from 'app/core/platform/platform.types';
import { map, take, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CdnFileType, ImageFile } from 'app/shared/shared.types';
import { AudioFile, AudioFormat, Track } from 'app/modules/user/releases/releases.types';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root'
})
export class PlatformService {
    private cdnUrl: string;

    constructor(private _httpClient: HttpClient) {
        if (!this._cdnUrl) {
            this._cdnUrl = new BehaviorSubject<string>(null);
        }
    }

    private _platform: ReplaySubject<PlatformSettings>;
    private _cdnUrl: BehaviorSubject<string>;
    public faqs$: Subject<FAQEntry[]> = new BehaviorSubject<FAQEntry[]>([]);

    set platform(value: PlatformSettings) {
        if (!this._platform) {
            this._platform = new ReplaySubject<PlatformSettings>(1);
        }
        this._platform.next(value);
        this._cdnUrl.next(value.cdn);
    }

    get platform$(): Observable<PlatformSettings> {
        if (!this._platform) {
            this._platform = new ReplaySubject<PlatformSettings>(1);
            this.get().pipe(take(1)).subscribe();
        }

        return this._platform.asObservable();
    }

    get loginLogoUrl$(): Observable<string> {
        return combineLatest([
            this._cdnUrl.asObservable(),
            this._platform,
        ]).pipe(map(([url, platform]) => {
            if (this.isDarkModeActive()) {
                return this.getFileSrc(platform.loginLogoDark, "logo");
            } else {
                return this.getFileSrc(platform.loginLogo, "logo");
            }
        }))
    }

    get loginBackgroundUrl$(): Observable<string> {
        return combineLatest([
            this._cdnUrl.asObservable(),
            this._platform,
        ]).pipe(map(([url, platform]) => {
            return this.getFileSrc(platform.loginBackground, "logo");
        }))
    }

    getFileSrc(file: ImageFile | AudioFile, type: CdnFileType) {
        if (!file?.fileName) {
            return "";
        }

        if (file.fileName.startsWith('https://')) {
            return file.fileName.replace("size=1024", "size=128");
        }

        if (!this.cdnUrl) {
            this.platform$.pipe(take(1)).subscribe(p => {
                this.cdnUrl = p.cdn;
            });
        }

        let urlParts = [];
        switch (type) {
            case "artwork":
                urlParts.push("artwork");
                break;
            case "avatar":
                urlParts.push("avatar");
                break;
            case "logo":
                urlParts.push("logo");
                break;
            case "favicon":
                urlParts.push("favicon");
                break;
        }

        //Add "_300" to the file name to get the 300px version
        let fileName = file.fileName;
        if (type === "artwork") {
            fileName = fileName.replace(".jpg", "_300.jpg");
            fileName = fileName.replace(".png", "_300.png");
        }
        urlParts.push(fileName);

        let url = this.cdnUrl + urlParts.join("/").replace(/\/\//g, "/");
        return url;
    }

    getAudioPreviewSrc(track: Track) {
        return `${environment.https ? "https" : "http"}://${environment.api}/api/media/${track.uuid}/${AudioFormat.Mp3_96}`;
    }

    get(): Observable<PlatformSettings> {
        return this._httpClient.get<PlatformSettings>('api/platform').pipe(
            tap((platform) => {
                if (!this._platform) {
                    this._platform = new ReplaySubject<PlatformSettings>(1);
                }
                this._platform.next(platform);
                this._cdnUrl.next(platform.cdn);
            })
        );
    }

    getImprint(): Observable<string> {
        return this._httpClient.get<any>('api/platform/docs/imprint').pipe(map((response) => {
            return response.imprintHtml;
        }));
    }

    update(platform: any): Observable<PlatformSettings> {
        return this._httpClient.patch<PlatformSettings>('api/platform', platform).pipe(
            map((response) => {
                this._platform.next(response);
                return response;
            })
        );
    }

    getFontColor(): string {
        return getComputedStyle(document.body).getPropertyValue('--fuse-text-default');
    }

    getAccentColor(): string {
        return getComputedStyle(document.body).getPropertyValue('--fuse-primary');
    }

    isDarkModeActive(): boolean {
        return document.body.classList.contains('dark');
    }

    getFAQs(): Observable<FAQEntry[]> {
        return this._httpClient.get<FAQEntry[]>('api/platform/faqs').pipe(tap((response) => {
            for (let faq of response) {
                faq.answer = faq.answer.replace(/\n/g, "<br />");
            }
            this.faqs$.next(response);
        }));
    }
}
